//shared/hooks/useChat.js
import { useState, useEffect, useRef, useContext } from "react";
import { useHttpClient } from "../../shared/hooks/http-hook";
import { useWebSocketSubscription } from "../../shared/context/websocket-context";
import { AuthContext } from "../../shared/context/auth-context";
import { ClinicaContext } from "../../shared/context/clinica-context";

const messageSound = new Audio("/sounds/message.wav");
messageSound.volume = 0.5;

const LLAMA_USER = {
  _id: "llama-assistant",
  name: "Dentinho",
  email: "ia@odontoht.com",
  avatar: null,
  role: "Assistente Virtual",
  isAI: true,
};

export const useChat = () => {
  const auth = useContext(AuthContext);
  const clinica = useContext(ClinicaContext);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [combinedList, setCombinedList] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(
    auth.playNotificationSound ?? true,
  );

  const [llamaMessages, setLlamaMessages] = useState([]);
  const [llamaUnreadCount, setLlamaUnreadCount] = useState(0);

  const [llamaLoading, setLlamaLoading] = useState(false);

  const { sendRequest } = useHttpClient();
  const messagesEndRef = useRef(null);

  const selectedConversationRef = useRef(null);
  const combinedListRef = useRef([]);
  const soundEnabledRef = useRef(true);

  // Carregar histórico do Llama da BD
  const loadLlamaHistory = async () => {
    if (!auth.userId || !clinica?.clinica?._id) return;

    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/llama/history?userId=${auth.userId}&clinicaId=${clinica.clinica._id}`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token },
      );

      setLlamaMessages(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar histórico Llama:", error);
    }
  };

  const loadLlamaUnreadCount = async () => {
    if (!auth.userId || !clinica?.clinica?._id) return;

    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/llama/unread?userId=${auth.userId}&clinicaId=${clinica.clinica._id}`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token },
      );
      setLlamaUnreadCount(response.data?.unreadCount || 0);
    } catch (error) {
      console.error("Erro ao carregar unread Llama:", error);
    }
  };

  // Sincronizar estado do som com auth
  useEffect(() => {
    setSoundEnabled(auth.playNotificationSound ?? true);
  }, [auth.playNotificationSound]);

  useEffect(() => {
    if (!auth.userId || !clinica?.clinica?._id) {
      return;
    }

    const loadInitialData = async () => {
      setLoadingUsers(true);
      await Promise.all([
        loadConversations(),
        loadAllUsers(),
        loadUnreadCount(),
        loadLlamaHistory(),
        loadLlamaUnreadCount(),
      ]);
      setLoadingUsers(false);
    };

    loadInitialData();
  }, [auth.userId, clinica?.clinica?._id]);

  // Combinar usuários com conversas
  // Combinar usuários com conversas + Llama
  useEffect(() => {
    // Criar entrada do Llama
    const lastLlamaMessage = llamaMessages[llamaMessages.length - 1];
    const llamaConversation = {
      id: "llama-assistant",
      user: LLAMA_USER,
      lastMessage: lastLlamaMessage
        ? {
            text: lastLlamaMessage.content,
            createdAt: lastLlamaMessage.timestamp || lastLlamaMessage.createdAt,
          }
        : null,
      unreadCount: llamaUnreadCount, // ← Usar estado
      hasConversation: llamaMessages.length > 0,
      isAI: true,
    };

    if (!allUsers.length) {
      setCombinedList([llamaConversation, ...conversations]);
      return;
    }

    const combined = allUsers.map((user) => {
      // Suportar tanto user como objeto {_id} quanto como string
      const existingConv = conversations.find((c) => {
        const convUserId = c.user?._id || c.user;
        return (
          convUserId === user._id ||
          convUserId?.toString() === user._id?.toString()
        );
      });

      if (existingConv) {
        return { ...existingConv, user };
      }

      const visibleConversationId = [auth.userId, user._id].sort().join("_");
      return {
        id: visibleConversationId,
        user,
        lastMessage: null,
        unreadCount: 0,
        hasConversation: false,
      };
    });

    // Adicionar Llama à lista
    combined.push(llamaConversation);

    // Ordenar: não lidas > conversas recentes > sem conversa
    combined.sort((a, b) => {
      if ((a.unreadCount || 0) !== (b.unreadCount || 0)) {
        return (b.unreadCount || 0) - (a.unreadCount || 0);
      }
      if (a.lastMessage && !b.lastMessage) return -1;
      if (!a.lastMessage && b.lastMessage) return 1;
      if (a.lastMessage && b.lastMessage) {
        return (
          new Date(b.lastMessage.createdAt) - new Date(a.lastMessage.createdAt)
        );
      }
      return (a.user?.name || "").localeCompare(b.user?.name || "");
    });

    setCombinedList(combined);
    combinedListRef.current = combined;
  }, [allUsers, conversations, auth.userId, llamaMessages, llamaUnreadCount]);

  // Scroll automático quando mensagens mudam OU conversa selecionada muda.
  // Inclui llamaMessages para acompanhar o streaming das respostas do Dentinho
  // (que usa um estado separado de messages).
  useEffect(() => {
    const timeout = setTimeout(() => {
      scrollToBottom();
    }, 100);
    return () => clearTimeout(timeout);
  }, [messages, llamaMessages, selectedConversation]);

  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);
  // ==================== WEBSOCKET ====================

  useWebSocketSubscription(
    async (parsedSocketData) => {
      // Processar mensagens de chat normal
      if (parsedSocketData.tabela === "messages") {
        const messageDoc = parsedSocketData.documento;

        if (parsedSocketData.tipoDeOperacao === "insert") {
          const isFromMe =
            messageDoc.from?._id?.toString() === auth.userId ||
            messageDoc.from?.toString() === auth.userId;

          if (!isFromMe) {
            handleNewMessage(messageDoc);
          }
        }

        if (parsedSocketData.tipoDeOperacao === "update") {
          handleMessageRead(messageDoc);
        }

        return; // Importante: sair aqui
      }

      // Processar mensagens do Llama
      if (parsedSocketData.tabela === "llamamessages") {
        const messageDoc = parsedSocketData.documento;

        if (parsedSocketData.tipoDeOperacao === "insert") {
          // Verificar se é para este utilizador
          const isForMe =
            messageDoc.user?.toString() === auth.userId ||
            messageDoc.user === auth.userId;

          if (!isForMe) return;

          // Se estou na conversa do Llama, ignorar WebSocket
          // (mensagens já são adicionadas localmente pelo sendLlamaMessage)
          if (selectedConversationRef.current?.id === "llama-assistant") {
            return;
          }

          setLlamaMessages((prev) => {
            // Verificar se já existe por ID
            const messageId = messageDoc._id?.toString?.() || messageDoc._id;
            const existsById = prev.some((m) => {
              const existingId = m._id?.toString?.() || m._id;
              return existingId === messageId;
            });
            if (existsById) return prev;

            // Verificar se existe mensagem temporária com mesmo conteúdo
            if (messageDoc.role === "assistant") {
              const tempMessage = prev.find(
                (m) =>
                  m._id?.startsWith?.("assistant-") &&
                  m.content === messageDoc.content,
              );
              if (tempMessage) {
                return prev.map((m) =>
                  m._id === tempMessage._id ? { ...m, _id: messageDoc._id } : m,
                );
              }
            }

            // Verificar duplicação por conteúdo para mensagens do user
            if (messageDoc.role === "user") {
              const existsByContent = prev.some(
                (m) => m.role === "user" && m.content === messageDoc.content,
              );
              if (existsByContent) return prev;
            }

            // É mensagem nova (provavelmente de job) - adicionar
            return [
              ...prev,
              {
                _id: messageDoc._id,
                role: messageDoc.role,
                content: messageDoc.content,
                timestamp: messageDoc.createdAt,
                from: { _id: "llama-assistant", name: "Assistente IA" },
              },
            ];
          });

          // Incrementar unread e tocar som se não estiver na conversa do Llama
          if (selectedConversationRef.current?.id !== "llama-assistant") {
            setLlamaUnreadCount((prev) => prev + 1);
            if (soundEnabledRef.current) {
              messageSound.play().catch(() => {});
            }
          }
        }

        return;
      }
    },
    [auth.userId, selectedConversation],
    "ChatComponent",
  );

  const loadAllUsers = async () => {
    try {
      const data = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/chat/users?clinicaId=${clinica.clinica._id}&userId=${auth.userId}`,
        "GET",
        null,
        {
          Authorization: "Bearer " + auth.token,
        },
      );
      setAllUsers(data.data || []);
    } catch (error) {
      console.error("❌ Error loading users:", error);
      setAllUsers([]);
    }
  };

  const handleNewMessage = (message) => {
    const isForMe = message.to?.toString() === auth.userId;
    const currentConversation = selectedConversationRef.current;
    const currentCombinedList = combinedListRef.current;

    // Tocar som se for mensagem para mim e som está habilitado
    if (isForMe && soundEnabledRef.current) {
      messageSound.play().catch(() => {});
    }

    // Buscar dados do remetente da lista combinada
    let senderData = null;
    if (!message.from?.name) {
      const conv = currentCombinedList.find(
        (c) => c.id === message.conversationId,
      );
      senderData = conv?.user;
    }
    // Enriquecer mensagem com dados do remetente
    let enrichedMessage = message;
    if (!message.from?.name && senderData) {
      enrichedMessage = {
        ...message,
        from: {
          _id: message.from?._id || message.from,
          name: senderData.name,
          avatar: senderData.avatar,
        },
      };
    }

    // Atualizar mensagens se a conversa está aberta

    if (currentConversation?.id === message.conversationId) {
      setMessages((prev) => {
        return [...prev, enrichedMessage];
      });

      // Se sou o destinatário, marcar como lida
      if (isForMe) {
        markAsRead(message.conversationId);
      }
    }

    // Atualizar sidebar
    setConversations((prev) => {
      const exists = prev.find((c) => c.id === message.conversationId);

      if (exists) {
        return prev.map((conv) => {
          if (conv.id === message.conversationId) {
            const isConversationOpen =
              currentConversation?.id === message.conversationId;

            return {
              ...conv,
              lastMessage: {
                text: message.message,
                createdAt: message.createdAt,
              },
              unreadCount:
                isForMe && !isConversationOpen
                  ? (conv.unreadCount || 0) + 1
                  : conv.unreadCount || 0,
            };
          }
          return conv;
        });
      }

      // Nova conversa (só se sou o destinatário)
      // Nova conversa (só se sou o destinatário)
      if (isForMe) {
        // Buscar dados completos do remetente da lista combinada
        const senderId = message.from?._id || message.from;
        const senderFromList = currentCombinedList.find(
          (c) =>
            c.user?._id === senderId || c.user?._id === senderId?.toString(),
        );

        const senderUser = senderFromList?.user || {
          _id: senderId,
          name: "Utilizador",
          avatar: null,
        };

        return [
          {
            id: message.conversationId,
            user: senderUser,
            lastMessage: {
              text: message.message,
              createdAt: message.createdAt,
            },
            unreadCount: 1,
          },
          ...prev,
        ];
      }

      return prev;
    });

    // Atualizar contador total de não lidas
    if (isForMe && currentConversation?.id !== message.conversationId) {
      setUnreadCount((prev) => prev + 1);
    }
  };

  const handleMessageRead = (message) => {
    // Atualizar status de leitura nas mensagens
    setMessages((prev) =>
      prev.map((msg) =>
        msg._id === message._id
          ? { ...msg, read: true, readAt: message.readAt }
          : msg,
      ),
    );
  };

  // ==================== API CALLS ====================

  const loadConversations = async () => {
    if (!auth.userId) return;

    try {
      const data = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/chat/conversations?userId=${auth.userId}`,
        "GET",
        null,
        {
          Authorization: "Bearer " + auth.token,
        },
      );
      setConversations(data.data || []);
    } catch (error) {
      console.error("Error loading conversations:", error);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      setLoading(true);
      const data = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/chat/messages/${conversationId}?userId=${auth.userId}`,
        "GET",
        null,
        {
          Authorization: "Bearer " + auth.token,
        },
      );
      setMessages(data.data || []);
    } catch (error) {
      console.error("Error loading messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    if (!auth.userId) return;
    try {
      const data = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/chat/unread?userId=${auth.userId}`,
        "GET",
        null,
        {
          Authorization: "Bearer " + auth.token,
        },
      );
      setUnreadCount(data.data?.unreadCount || 0);
    } catch (error) {
      console.error("Error loading unread count:", error);
    }
  };

  const sendMessage = async (message) => {
    if (!selectedConversation || !message.trim()) return;

    try {
      const data = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/chat/send`,
        "POST",
        JSON.stringify({
          fromId: auth.userId,
          toId: selectedConversation.user._id,
          message: message,
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );

      const newMessage = data.data;

      // Adicionar mensagem localmente (otimista)
      setMessages((prev) => [...prev, newMessage]);

      // Atualizar última mensagem na sidebar
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === selectedConversation.id
            ? {
                ...conv,
                lastMessage: {
                  text: message,
                  createdAt: new Date(),
                },
              }
            : conv,
        ),
      );

      return newMessage;
    } catch (error) {
      console.error("Error sending message:", error);
      throw error;
    }
  };

  const sendLlamaMessage = async (userMessage) => {
    setLlamaLoading(true);

    // Guardar mensagem do utilizador na BD
    // let savedUserMessage;
    // try {
    //   const response = await sendRequest(
    //     `${process.env.REACT_APP_BACKEND_LINK}/api/llama/message`,
    //     "POST",
    //     JSON.stringify({
    //       userId: auth.userId,
    //       clinicaId: clinica.clinica._id,
    //       role: "user",
    //       content: userMessage,
    //     }),
    //     {
    //       "Content-Type": "application/json",
    //       Authorization: "Bearer " + auth.token,
    //     },
    //   );
    //   savedUserMessage = response.data;
    // } catch (error) {
    //   console.error("Erro ao guardar mensagem:", error);
    //   setLlamaLoading(false);
    //   return;
    // }

    // const newUserMessage = {
    //   ...savedUserMessage,
    //   from: { _id: auth.userId, name: auth.nome },
    // };

    // // Adicionar mensagem do utilizador
    // setLlamaMessages((prev) => [...prev, newUserMessage]);

    // Mostrar mensagem do utilizador imediatamente sem esperar BD
    const tempUserMessage = {
      _id: `user-${Date.now()}`,
      role: "user",
      content: userMessage,
      timestamp: new Date().toISOString(),
      from: { _id: auth.userId, name: auth.nome },
    };
    setLlamaMessages((prev) => [...prev, tempUserMessage]);

    // Guardar na BD em paralelo sem bloquear
    sendRequest(
      `${process.env.REACT_APP_BACKEND_LINK}/api/llama/message`,
      "POST",
      JSON.stringify({
        userId: auth.userId,
        clinicaId: clinica.clinica._id,
        role: "user",
        content: userMessage,
      }),
      {
        "Content-Type": "application/json",
        Authorization: "Bearer " + auth.token,
      },
    ).catch((err) => console.error("Erro ao guardar mensagem:", err));

    // Preparar contexto (últimas 10 mensagens)
    const context = [...llamaMessages, tempUserMessage].slice(-4).map((m) => ({
      role: m.role,
      content: m.content,
    }));

    // Dados do usuário para personalização
    const user = {
      id: auth.userId,
      nome: auth.nome,
      cargo: auth.role,
    };

    const dadosClinica = {
      _id: clinica.clinica?._id,
      nome: clinica.clinica?.nome,
      endereco: clinica.clinica?.endereco,
      cidade: clinica.clinica?.cidade,
      ilha: clinica.clinica?.ilha,
      nif: clinica.clinica?.nif,
      codigoPostal: clinica.clinica?.codigoPostal,
      contacto: clinica.clinica?.contacto,
      contacto2: clinica.clinica?.contacto2,
      contacto3: clinica.clinica?.contacto3,
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_LINK}/api/llama/chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.token}`,
          },
          body: JSON.stringify({
            message: userMessage,
            context,
            user,
            dadosClinica,
          }),
          signal: controller.signal,
        },
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Erro ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      // Adicionar mensagem vazia do assistente
      // Adicionar mensagem vazia do assistente (será preenchida pelo streaming)
      const assistantMsgId = `assistant-${Date.now()}`;
      setLlamaMessages((prev) => [
        ...prev,
        {
          _id: assistantMsgId,
          role: "assistant",
          content: "",
          timestamp: new Date().toISOString(),
          from: { _id: "llama-assistant", name: "Dentinho" },
          isStreaming: true,
        },
      ]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk
          .split("\n")
          .filter((line) => line.startsWith("data:"));

        for (const line of lines) {
          const data = line.replace("data: ", "");
          if (data === "[DONE]") continue;

          try {
            const json = JSON.parse(data);
            if (json.content) {
              setLlamaMessages((prev) => {
                const updated = [...prev];
                const lastIdx = updated.length - 1;
                updated[lastIdx] = {
                  ...updated[lastIdx],
                  content: updated[lastIdx].content + json.content,
                };
                return updated;
              });
            }
          } catch (e) {}
        }
      }

      // Guardar resposta completa do assistente na BD
      //   setLlamaMessages((prev) => {
      //     const lastMessage = prev[prev.length - 1];
      //     if (lastMessage?.role === "assistant" && lastMessage.content) {
      //       // Guardar assincronamente (não bloquear UI)
      //       sendRequest(
      //         `${process.env.REACT_APP_BACKEND_LINK}/api/llama/message`,
      //         "POST",
      //         JSON.stringify({
      //           userId: auth.userId,
      //           clinicaId: clinica.clinica._id,
      //           role: "assistant",
      //           content: lastMessage.content,
      //         }),
      //         {
      //           "Content-Type": "application/json",
      //           Authorization: "Bearer " + auth.token,
      //         }
      //       ).catch((err) => console.error("Erro ao guardar resposta IA:", err));
      //     }
      //     return prev;
      //   });
      // } catch (error) {
      // Guardar resposta completa do assistente na BD
    } catch (error) {
      console.error("Erro Llama:", error);
      console.error("Erro Llama - nome:", error.name);
      console.error("Erro Llama - mensagem:", error.message);
      console.error("Erro Llama - tipo:", error.type);
      setLlamaMessages((prev) => [
        ...prev,
        {
          _id: `error-${Date.now()}`,
          role: "assistant",
          content: "Desculpe, ocorreu um erro ao comunicar com o assistente.",
          timestamp: new Date().toISOString(),
          from: { _id: "llama-assistant", name: "Dentinho" },
        },
      ]);
    } finally {
      setLlamaLoading(false);
      // Marcar última mensagem como não streaming e guardar na BD
      setLlamaMessages((prev) => {
        const updated = [...prev];
        if (updated.length > 0) {
          const lastMsg = updated[updated.length - 1];
          updated[updated.length - 1] = {
            ...lastMsg,
            isStreaming: false,
          };

          // Guardar na BD apenas se tiver ID temporário
          if (
            lastMsg.role === "assistant" &&
            lastMsg._id?.startsWith("assistant-")
          ) {
            sendRequest(
              `${process.env.REACT_APP_BACKEND_LINK}/api/llama/message`,
              "POST",
              JSON.stringify({
                userId: auth.userId,
                clinicaId: clinica.clinica._id,
                role: "assistant",
                content: lastMsg.content,
              }),
              {
                "Content-Type": "application/json",
                Authorization: "Bearer " + auth.token,
              },
            ).catch((err) =>
              console.error("Erro ao guardar resposta IA:", err),
            );
          }
        }
        return updated;
      });
    }
  };

  const clearLlamaHistory = async () => {
    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/llama/history?userId=${auth.userId}&clinicaId=${clinica.clinica._id}`,
        "DELETE",
        null,
        { Authorization: "Bearer " + auth.token },
      );
      setLlamaMessages([]);
    } catch (error) {
      console.error("Erro ao limpar histórico:", error);
    }
  };

  const markAsRead = async (conversationId) => {
    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/chat/read/${conversationId}?userId=${auth.userId}`,
        "PATCH",
        null,
        {
          Authorization: "Bearer " + auth.token,
        },
      );

      // Atualizar contador localmente
      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.id === conversationId) {
            const wasUnread = conv.unreadCount > 0;
            if (wasUnread) {
              setUnreadCount((prevTotal) =>
                Math.max(0, prevTotal - conv.unreadCount),
              );
            }
            return { ...conv, unreadCount: 0 };
          }
          return conv;
        }),
      );
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  const selectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    selectedConversationRef.current = conversation;

    // Se for Llama, não carregar do backend
    if (conversation.id === "llama-assistant") {
      setMessages([]);

      // Marcar mensagens do Llama como lidas
      if (llamaUnreadCount > 0) {
        try {
          await sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/llama/read?userId=${auth.userId}&clinicaId=${clinica.clinica._id}`,
            "PATCH",
            null,
            { Authorization: "Bearer " + auth.token },
          );
          setLlamaUnreadCount(0);
        } catch (error) {
          console.error("Erro ao marcar como lidas:", error);
        }
      }
      return;
    }
    // Carregar mensagens se houver lastMessage OU se a conversa pode existir
    // (hasConversation pode estar desatualizado após receber mensagem via WebSocket)
    if (
      conversation.lastMessage ||
      conversation.unreadCount > 0 ||
      conversation.hasConversation !== false
    ) {
      await loadMessages(conversation.id);
    } else {
      setMessages([]);
    }

    if (conversation.unreadCount > 0) {
      await markAsRead(conversation.id);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
  };

  const toggleSound = (value) => {
    setSoundEnabled(value);
  };

  const clearSelection = () => {
    setSelectedConversation(null);
    selectedConversationRef.current = null;
    setMessages([]);
  };

  return {
    conversations: combinedList,
    selectedConversation,
    messages:
      selectedConversation?.id === "llama-assistant" ? llamaMessages : messages,
    loading: selectedConversation?.id === "llama-assistant" ? false : loading, // Nunca mostrar spinner para Llama
    unreadCount: unreadCount + llamaUnreadCount,
    messagesEndRef,
    sendMessage:
      selectedConversation?.id === "llama-assistant"
        ? sendLlamaMessage
        : sendMessage,
    selectConversation,
    loadingUsers,
    soundEnabled,
    toggleSound,
    isLlamaChat: selectedConversation?.id === "llama-assistant",
    llamaLoading, // Usado apenas para desabilitar input
    clearLlamaHistory,
    clearSelection,
  };
};
